/**
 * The editor's model of a section (DOP-580): a list of items, each either a
 * generated paragraph (with its anchor) or a person's own, and the edit
 * script that turns the generated body into the list. The generated body is
 * never changed; the script is what the server stores and re-applies on the
 * next version, so the editor always writes ops AGAINST THE GENERATED TEXT
 * even when it opened on an already-edited section.
 */

import type {
	DealScreeningChart,
	DealScreeningEditOp,
	DealScreeningEditOpChart,
	DealScreeningParagraph,
	DealScreeningSection,
	DealScreeningSourceNote,
} from "../../types/dealScreening";
import { sentSource } from "./documentSource";
import { type FormatSpan, sentenceFormats, toStoredOffsets } from "./memoFormatModel";
import { type EditTable, editTableOf, tableKey, toOpTable } from "./tableEditModel";

export interface EditItem {
	/** The generated paragraph this stands for; null for a person's addition. */
	anchor: string | null;
	lead: string;
	text: string;
	kind: "prose" | "bullets";
	/** A table paragraph as the reader saw it when editing started. */
	table: DealScreeningParagraph["table"] | null;
	/** The generated wording, to tell a rewrite from an untouched paragraph. */
	generatedLead: string;
	generatedText: string;
	/** A table as the person left it (DOP-637); absent means as it was. */
	tableNow?: EditTable | null;
	/** A chart paragraph (DOP-655): the chart is the whole item. */
	chart?: DealScreeningChart | null;
	/** Where the person's words came from (DOP-637). */
	notes?: DealScreeningSourceNote[];
	/**
	 * The editor block this came from, as it was when editing started. It
	 * tells a person's own paragraph (no anchor) edited in place from one
	 * taken out and another added.
	 */
	baseId?: string | null;
	/** The generated table, to tell a rewritten table from an untouched one. */
	generatedTable?: EditTable | null;
	/** Bold, italic, underline, strikethrough: runs of `text` (DOP-641). */
	formats?: FormatSpan[];
	/** A bullets item numbered 1, 2, 3 (DOP-641). */
	ordered?: boolean;
	/** How the generated paragraph was laid out, to tell a list made prose (or back). */
	generatedKind?: "prose" | "bullets";
	generatedOrdered?: boolean;
}

/**
 * The script with additions that share a place kept in the order they were
 * typed. The server puts an insert right after the paragraph it names, so
 * two in a row after the same one would come out reversed (a table, then a
 * chart under it, read chart then table): each such run is sent backwards.
 */
function inOrder(ops: DealScreeningEditOp[]): DealScreeningEditOp[] {
	const out: DealScreeningEditOp[] = [];
	let run: DealScreeningEditOp[] = [];
	const flush = () => {
		out.push(...run.reverse());
		run = [];
	};
	// Additions after one paragraph, and additions at the very start
	// (``after`` null: the server puts each at position 0), each land in
	// front of the one sent before them. Ones at the end are appended, and
	// keep their order as sent.
	const placeKey = (op: DealScreeningEditOp): string | null =>
		op.op !== "insert" || op.at_end ? null : (op.after ?? "");
	ops.forEach((op) => {
		const key = placeKey(op);
		const sameRun = run.length > 0 && key !== null && key === placeKey(run[0]);
		if (!sameRun) flush();
		if (key !== null) run.push(op);
		else out.push(op);
	});
	flush();
	return out;
}

/**
 * A chart as the save sends it (DOP-655): what is drawn, and each source as
 * a note — a person's words as they are, a document as the place in it, for
 * the server to read again.
 */
export function toOpChart(chart: DealScreeningChart): DealScreeningEditOpChart {
	const notes = (chart.sources ?? []).flatMap((source) => {
		if (source.kind === "user") return source.note?.trim() ? [{ note: source.note.trim() }] : [];
		if (!source.attachment_id) return [];
		return [
			{
				note: "",
				source: sentSource({
					attachment_id: source.attachment_id,
					page: source.page,
					sheet: source.sheet,
					cell: source.cell,
					quote: source.quote,
				}),
			},
		];
	});
	return {
		chart_type: chart.chart_type,
		title: chart.title ?? null,
		data: chart.data,
		...(notes.length > 0 ? { notes } : {}),
	};
}

/** A table item as it stands now. */
export const currentTable = (item: EditItem): EditTable | null =>
	item.tableNow ?? editTableOf(item.table);

export const paragraphText = (paragraph: DealScreeningParagraph): string =>
	paragraph.sentences.map((s) => s.text).join(" ");

/** The editor opens on what the reader sees: the edited view when there is one. */
export function itemsOf(section: DealScreeningSection): EditItem[] {
	const generated = new Map(
		(section.generated_paragraphs ?? section.paragraphs).map((p) => [p.anchor ?? "", p]),
	);
	return section.paragraphs.map((paragraph) => {
		const anchor = paragraph.edit ? (paragraph.anchor ?? null) : (paragraph.anchor ?? null);
		const origin = anchor ? generated.get(anchor) : undefined;
		return {
			anchor: paragraph.edit?.op === "insert" ? null : anchor,
			lead: paragraph.lead ?? "",
			text: paragraphText(paragraph),
			kind: paragraph.kind === "bullets" ? "bullets" : "prose",
			table: paragraph.kind === "table" ? (paragraph.table ?? null) : null,
			...(paragraph.kind === "chart" && paragraph.chart ? { chart: paragraph.chart } : {}),
			generatedLead: origin?.lead ?? "",
			generatedText: origin ? paragraphText(origin) : "",
			generatedTable: editTableOf(origin?.table),
			ordered: Boolean(paragraph.ordered),
			generatedKind: origin?.kind === "bullets" ? "bullets" : "prose",
			generatedOrdered: Boolean(origin?.ordered),
		};
	});
}

/**
 * Sentences from typed text: split on sentence ends, one per line also works.
 *
 * `known` are the paragraph's generated sentences. Each one still in the text
 * word for word is cut out as its own sentence first, whatever letter the
 * next one starts with, so the server matches it and keeps its source
 * (services/edits.py). Only the rest is split by punctuation.
 */
export function sentencesOf(text: string, known: string[] = []): string[] {
	const split = (part: string) =>
		part
			.split(/(?<=[.!?])\s+(?=[A-Z0-9"“(])|\n+/)
			.map((s) => s.trim())
			.filter(Boolean);
	const found = known
		.map((sentence) => sentence.trim())
		.filter(Boolean)
		.sort((a, b) => b.length - a.length);
	const out: string[] = [];
	let rest = text;
	while (rest.trim()) {
		let at = -1;
		let match = "";
		for (const sentence of found) {
			const i = rest.indexOf(sentence);
			if (i >= 0 && (at < 0 || i < at)) {
				at = i;
				match = sentence;
			}
		}
		if (at < 0) {
			out.push(...split(rest));
			break;
		}
		out.push(...split(rest.slice(0, at)), match);
		rest = rest.slice(at + match.length);
	}
	return out;
}

/**
 * The edit script: every generated paragraph missing from the items is a
 * delete; a generated paragraph whose wording changed is a replace; an item
 * with no anchor is an insert after the nearest generated paragraph before
 * it (or at the start, or at the end).
 */
export function opsOf(
	section: DealScreeningSection,
	items: EditItem[],
	/** The section's title as the person left it (DOP-641). */
	title?: string,
): DealScreeningEditOp[] {
	const generated = section.generated_paragraphs ?? section.paragraphs;
	const present = new Set(items.map((item) => item.anchor).filter(Boolean));
	const knownSentences = new Map(
		generated.map((p) => [p.anchor ?? "", p.sentences.map((sentence) => sentence.text)]),
	);
	const ops: DealScreeningEditOp[] = [];
	// A rename is part of the whole script, re-sent with every save while it
	// stands, as the paragraphs' edits are.
	const renamed = title?.replace(/\s+/g, " ").trim();
	if (renamed && renamed !== (section.generated_title ?? section.title)) {
		ops.push({ op: "title", title: renamed });
	}
	for (const paragraph of generated) {
		if (paragraph.anchor && !present.has(paragraph.anchor)) {
			ops.push({ op: "delete", anchor: paragraph.anchor });
		}
	}
	/** Where an item with no generated paragraph behind it goes. */
	const place = (index: number): Pick<DealScreeningEditOp, "after" | "at_end"> => {
		const before = items.slice(0, index).reverse().find((i) => i.anchor);
		if (before) return { after: before.anchor };
		if (index === items.length - 1 && items.length > 1) return { at_end: true };
		return { after: null };
	};
	items.forEach((item, index) => {
		if (item.chart) {
			// A chart is never generated (DOP-655): it is an addition, sent
			// whole with every save, with where its values came from.
			const paragraph = { kind: "chart" as const, sentences: [], chart: toOpChart(item.chart) };
			if (item.anchor) ops.push({ op: "replace", anchor: item.anchor, paragraph });
			else ops.push({ op: "insert", ...place(index), paragraph });
			return;
		}
		if (item.table) {
			// A table a person rewrote is the whole table as they left it
			// (DOP-637); one they did not touch is left to the generated body.
			const now = currentTable(item);
			if (item.anchor && now && tableKey(now) !== tableKey(item.generatedTable ?? null)) {
				ops.push({
					op: "replace",
					anchor: item.anchor,
					paragraph: { kind: "table", sentences: [], table: toOpTable(now) },
				});
			} else if (!item.anchor && now && now.rows.some((row) => row.some((cell) => cell.trim()))) {
				// A table with no generated one behind it (DOP-653) is an
				// addition, sent whole with every save like a person's paragraph.
				ops.push({
					op: "insert",
					...place(index),
					paragraph: { kind: "table", sentences: [], table: toOpTable(now) },
				});
			}
			return;
		}
		// The place in a document is sent; its name is the server's to read.
		const notes = (item.notes ?? []).map((note) =>
			note.source ? { ...note, source: sentSource(note.source) } : note,
		);
		// A bullet is one sentence of the list, however many full stops it
		// holds: a question reads "Topic. Question?" (DOP-637).
		const sentences =
			item.kind === "bullets"
				? item.text.split("\n").map((line) => line.trim()).filter(Boolean)
				: sentencesOf(item.text, knownSentences.get(item.anchor ?? "") ?? []);
		// Each range as the server counts characters (code points, codex r2).
		const formats = sentenceFormats(item.text, item.formats ?? [], sentences).map(
			(f) => toStoredOffsets(sentences[f.sentence], [f])[0],
		);
		const ordered = item.kind === "bullets" && Boolean(item.ordered);
		const paragraph = {
			lead: item.lead.trim() || null,
			kind: item.kind,
			sentences,
			...(notes.length > 0 ? { notes } : {}),
			...(ordered ? { ordered } : {}),
			...(formats.length > 0 ? { formats } : {}),
		};
		if (item.anchor) {
			// Formatting and layout are edits too (DOP-641): a list made prose,
			// a list numbered, words put in italics.
			const laidOut =
				item.kind !== (item.generatedKind ?? item.kind) || ordered !== Boolean(item.generatedOrdered);
			if (
				item.text.trim() !== item.generatedText.trim() ||
				item.lead.trim() !== item.generatedLead.trim() ||
				notes.length > 0 ||
				formats.length > 0 ||
				laidOut
			) {
				ops.push({ op: "replace", anchor: item.anchor, paragraph });
			}
			return;
		}
		ops.push({ op: "insert", ...place(index), paragraph });
	});
	return inOrder(ops);
}
